/**
 * Strings of the threads screens (2026-10-29): the thread panel, the
 * threads list and the create form, the bar under a message, the sidebar
 * rows, the menus, the audit log lines and the errors.
 */
import type { Dict } from './core';

export const threadsEn: Dict = {
  // the channel header and the list
  'th.threads': 'Threads',
  'th.off': 'Threads are turned off right now',
  'th.list.title': 'Threads',
  'th.list.active': 'Active',
  'th.list.archived': 'Archived',
  'th.list.other': 'Other active threads',
  'th.list.joinedPrivate': 'Your private threads',
  'th.list.allPrivate': 'All private threads',
  'th.list.none': 'No threads here yet. Start one to talk about something on the side.',
  'th.list.noneArchived': 'No archived threads.',
  'th.list.more': 'Show older',
  'th.list.error': "Couldn't load the threads.",
  'th.new': 'New thread',
  'th.row.count': { one: '{n} message', other: '{n} messages' },
  'th.row.members': { one: '{n} member', other: '{n} members' },
  'th.row.private': 'Private',
  'th.row.archived': 'Archived',
  'th.row.locked': 'Locked',
  'th.row.joined': 'Joined',
  // the create form
  'th.create.title': 'New thread',
  'th.create.fromMessage': 'Thread from a message',
  'th.create.name': 'Thread name',
  'th.create.namePh': 'Give it a short name',
  'th.create.first': 'Starter message',
  'th.create.firstPh': 'Start the conversation (optional)',
  'th.create.private': 'Private thread',
  'th.create.privateSub': 'Only the people you add, and moderators, can see it.',
  'th.create.privateNo': "You can't start private threads in this channel.",
  'th.create.archive': 'Hide after inactivity',
  'th.create.submit': 'Create thread',
  'th.create.cancel': 'Cancel',
  'th.create.nameRequired': 'Give the thread a name.',
  'th.create.done': 'Thread created',
  // auto-archive choices
  'th.archive.60': '1 hour',
  'th.archive.1440': '24 hours',
  'th.archive.4320': '3 days',
  'th.archive.10080': '1 week',
  // the bar under a message and the system message
  'th.bar.open': 'Open thread {name}',
  'th.bar.count': { one: '{n} message', other: '{n} messages' },
  'th.bar.none': 'No replies yet',
  'th.bar.last': 'last reply {time}',
  'th.sys.started': 'started a thread:',
  'th.sys.seeAll': 'See all threads',
  // the thread panel
  'th.panel.label': 'Thread {name}',
  'th.panel.close': 'Close thread',
  'th.panel.back': 'Back to the thread',
  'th.panel.members': 'Thread members',
  'th.panel.pins': 'Pinned in this thread',
  'th.panel.search': 'Search this thread',
  'th.panel.more': 'Thread options',
  'th.panel.join': 'Join',
  'th.panel.joined': 'You joined the thread',
  'th.panel.left': 'You left the thread',
  'th.start.by': 'Started by {name}',
  'th.start.title': 'Welcome to {name}!',
  'th.start.sub': 'This is the start of the thread.',
  'th.start.original': 'Original message',
  'th.ph': 'Message {name}',
  'th.archived.note': 'This thread is archived. Send a message to bring it back.',
  'th.archived.noteMod': 'This thread is archived.',
  'th.archived.unarchive': 'Unarchive',
  'th.locked.note': 'This thread is locked. Only moderators can send messages.',
  // the menu
  'th.menu.join': 'Join thread',
  'th.menu.leave': 'Leave thread',
  'th.menu.markRead': 'Mark as read',
  'th.menu.rename': 'Rename thread',
  'th.menu.renameBody': 'A short name everyone sees.',
  'th.menu.archive': 'Archive thread',
  'th.menu.unarchive': 'Unarchive thread',
  'th.menu.lock': 'Lock thread',
  'th.menu.unlock': 'Unlock thread',
  'th.menu.invitable': 'Members can invite others',
  'th.menu.hideAfter': 'Hide after inactivity',
  'th.menu.delete': 'Delete thread',
  'th.menu.deleteTitle': 'Delete this thread?',
  'th.menu.deleteBody':
    'The thread and every message in it are deleted for good. This can’t be undone.',
  'th.menu.deleted': 'Thread deleted',
  'th.menu.archived': 'Thread archived',
  'th.menu.unarchived': 'Thread unarchived',
  'th.menu.locked': 'Thread locked',
  'th.menu.unlocked': 'Thread unlocked',
  'th.menu.renamed': 'Thread renamed',
  // a message's menu and toolbar
  'th.msg.create': 'Create thread',
  'th.msg.open': 'Open thread',
  // the members view
  'th.mem.title': 'Members',
  'th.mem.add': 'Add people',
  'th.mem.addPh': 'Find someone to add',
  'th.mem.remove': 'Remove from thread',
  'th.mem.removed': 'Removed from the thread',
  'th.mem.added': 'Added to the thread',
  'th.mem.none': 'Nobody joined yet.',
  'th.mem.noMatch': 'Nobody by that name can join.',
  'th.mem.error': "Couldn't load the members.",
  // the search and pins views
  'th.search.ph': 'Search this thread',
  'th.search.none': 'Nothing found in this thread.',
  'th.search.results': { one: '{n} result', other: '{n} results' },
  'th.pins.none': 'Nothing is pinned in this thread yet.',
  // the sidebar
  'th.side.label': 'Threads in {name}',
  'th.side.row': '{name}, thread',
  // the audit log
  'th.audit.create': '{actor} started the thread {thread} in {channel}',
  'th.audit.update': '{actor} updated the thread {thread}',
  'th.audit.rename': '{actor} renamed the thread {from} to {thread}',
  'th.audit.archive': '{actor} archived the thread {thread}',
  'th.audit.unarchive': '{actor} unarchived the thread {thread}',
  'th.audit.lock': '{actor} locked the thread {thread}',
  'th.audit.unlock': '{actor} unlocked the thread {thread}',
  'th.audit.delete': '{actor} deleted the thread {thread}',
  'th.audit.f': 'Threads',
  // the role and channel editors (Discord's thread permissions)
  'ns.perm.CREATE_PUBLIC_THREADS': 'Create public threads',
  'ns.permd.CREATE_PUBLIC_THREADS':
    'Start threads everyone in the channel can see and join, from a message or from the Threads list.',
  'ns.perm.CREATE_PRIVATE_THREADS': 'Create private threads',
  'ns.permd.CREATE_PRIVATE_THREADS':
    'Start invite-only threads that only the people added, and moderators, can see.',
  'ns.perm.SEND_MESSAGES_IN_THREADS': 'Send messages in threads',
  'ns.permd.SEND_MESSAGES_IN_THREADS':
    'Write in threads. This works on its own: without Send messages, members can still talk in threads.',
  'ns.perm.MANAGE_THREADS': 'Manage threads',
  'ns.permd.MANAGE_THREADS':
    'Rename, archive, lock and delete threads, set their slowmode, see every private thread and remove people from threads.',
  // errors (Discord's codes)
  'ns.err.50083': 'This thread is archived. Unarchive it first.',
  'ns.err.30033': 'This thread is full.',
  'ns.err.160004': 'That message already has a thread.',
  'ns.err.160005': 'This thread is locked. Only moderators can change it.',
  'ns.err.160006': 'The server has too many active threads. Archive some first.',
};
